import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronRight, HelpCircle, Search, MessageCircle, Shield, Users, Gamepad2 } from 'lucide-react';
import SEO from '../components/SEO';

const FAQ: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedSections, setExpandedSections] = useState<string[]>(['general']);

  const categories = [
    { id: 'all', label: 'Tümü', icon: HelpCircle },
    { id: 'general', label: 'Genel', icon: MessageCircle },
    { id: 'server', label: 'Sunucu', icon: Gamepad2 },
    { id: 'account', label: 'Hesap', icon: Users },
    { id: 'rules', label: 'Kurallar', icon: Shield }
  ];

  const faqSections = [
    {
      id: 'general',
      title: 'Genel Sorular',
      icon: MessageCircle,
      questions: [
        {
          question: 'Aedon Community nedir?',
          answer: 'Aedon Community, FiveM sunucuları için özel olarak tasarlanmış bir topluluk platformudur. En iyi roleplay deneyimini sunmak için kurulmuştur ve sürekli gelişen bir yapıya sahiptir.'
        },
        {
          question: 'Nasıl kayıt olabilirim?',
          answer: 'Ana sayfadaki "Kayıt Ol" butonuna tıklayarak veya header menüsünden "Kayıt Ol" seçeneğini seçerek kolayca kayıt olabilirsiniz. E-posta adresiniz ve güçlü bir şifre ile hesap oluşturabilirsiniz.'
        },
        {
          question: 'Destek nasıl alabilirim?',
          answer: 'Destek almak için Discord sunucumuzu kullanabilir, İletişim sayfasından mesaj gönderebilir veya adminlerimizle doğrudan iletişime geçebilirsiniz. Genellikle 24 saat içinde yanıt veririz.'
        }
      ]
    },
    {
      id: 'server',
      title: 'Sunucu Soruları',
      icon: Gamepad2,
      questions: [
        {
          question: 'Sunuculara nasıl katılabilirim?',
          answer: 'Sunucularımız sayfasından istediğiniz sunucuyu seçin ve "Form Doldur" butonuna tıklayın. Başvuru formunu doldurduktan sonra, başvurunuz adminler tarafından değerlendirilecek ve onaylandığında Discord linkine erişim sağlayacaksınız.'
        },
        {
          question: 'Sunucu başvurum neden reddedildi?',
          answer: 'Başvurular genellikle eksik bilgi, uygunsuz içerik veya kurallara uygun olmayan cevaplar nedeniyle reddedilir. Reddedilen başvurular için "Tekrar Başvur" butonunu kullanarak yeni bir başvuru gönderebilirsiniz.'
        },
        {
          question: 'Sunucu performansı nasıl?',
          answer: 'Sunucularımız yüksek performanslı donanım üzerinde çalışır ve %99+ uptime garantisi sunar. Ping değerleri genellikle 20-50ms arasındadır ve oyun deneyimini etkilemez.'
        },
        {
          question: 'Sunucu güncellemeleri ne sıklıkla yapılır?',
          answer: 'Sunucularımız düzenli olarak güncellenir. Major güncellemeler ayda bir, minor güncellemeler haftalık olarak yapılır. Güncellemeler Discord\'da duyurulur.'
        }
      ]
    },
    {
      id: 'account',
      title: 'Hesap Soruları',
      icon: Users,
      questions: [
        {
          question: 'Şifremi unuttum, ne yapmalıyım?',
          answer: 'Giriş sayfasındaki "Şifremi Unuttum" linkine tıklayarak e-posta adresinize şifre sıfırlama bağlantısı gönderebilirsiniz. E-postanızı kontrol edin ve verilen talimatları takip edin.'
        },
        {
          question: 'Hesabımı nasıl silebilirim?',
          answer: 'Hesap silme işlemi için lütfen adminlerimizle iletişime geçin. İletişim sayfasından veya Discord sunucumuzdan bize ulaşabilirsiniz.'
        },
        {
          question: 'Hesap bilgilerimi nasıl güncelleyebilirim?',
          answer: 'Profil sayfanızdan hesap bilgilerinizi güncelleyebilirsiniz. Kullanıcı adı, e-posta ve diğer kişisel bilgilerinizi değiştirebilirsiniz.'
        }
      ]
    },
    {
      id: 'rules',
      title: 'Kural Soruları',
      icon: Shield,
      questions: [
        {
          question: 'Sunucu kuralları nelerdir?',
          answer: 'Sunucu kurallarımız roleplay deneyimini korumak için tasarlanmıştır. Temel kurallar: Fail RP yapmamak, Meta Gaming yapmamak, Power Gaming yapmamak, Combat Logging yapmamak ve genel nezaket kurallarına uymaktır.'
        },
        {
          question: 'Kural ihlali durumunda ne olur?',
          answer: 'Kural ihlalleri durumunda adminlerimiz durumu değerlendirir ve uygun cezayı verir. Ceza türleri uyarıdan kalıcı ban\'a kadar değişebilir. Her durum bireysel olarak değerlendirilir.'
        }
      ]
    }
  ];

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const filteredSections = faqSections.filter(section => {
    if (selectedCategory === 'all') return true;
    return section.id === selectedCategory;
  }).map(section => ({
    ...section,
    questions: section.questions.filter(q => 
      q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(section => section.questions.length > 0);

  return (
    <>
      <SEO 
        title="Sıkça Sorulan Sorular - FAQ | Aedon Community" 
        description="Aedon Community hakkında sıkça sorulan sorular ve cevapları. Sunucu, hesap, kurallar ve genel konularda yardım alın."
        keywords="FAQ, sıkça sorulan sorular, yardım, Aedon Community, FiveM, roleplay"
      />
      
      <div className="min-h-screen pt-20 bg-dark-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-gaming font-bold text-white mb-4">
              Sıkça Sorulan Sorular
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Aedon Community hakkında merak ettiğiniz her şeyi burada bulabilirsiniz
            </p>
          </motion.div>

          {/* Search and Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-12"
          >
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-8">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Sorunuzu arayın..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-dark-800 border border-dark-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-lg"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category) => {
                const IconComponent = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                      selectedCategory === category.id
                        ? 'bg-white text-dark-900 shadow-lg'
                        : 'bg-dark-700 text-gray-300 hover:bg-dark-600'
                    }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    <span>{category.label}</span>
                  </button>
                );
              })}
            </div>
          </motion.div>

          {/* FAQ Sections */}
          <div className="space-y-6">
            {filteredSections.map((section, index) => {
              const Icon = section.icon;
              const isExpanded = expandedSections.includes(section.id);
              
              return (
                <motion.div
                  key={section.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="card"
                >
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full flex items-center justify-between text-left"
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className="w-6 h-6 text-primary-400" />
                      <h2 className="text-xl font-semibold text-white">{section.title}</h2>
                      <span className="text-sm text-gray-400">({section.questions.length} soru)</span>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                  </button>

                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-6 space-y-4"
                    >
                      {section.questions.map((faq, faqIndex) => (
                        <div key={faqIndex} className="bg-dark-700 p-4 rounded-lg">
                          <h3 className="text-lg font-semibold text-white mb-3 flex items-start space-x-2">
                            <HelpCircle className="w-5 h-5 text-primary-400 mt-0.5 flex-shrink-0" />
                            <span>{faq.question}</span>
                          </h3>
                          <div className="ml-7">
                            <p className="text-gray-300 leading-relaxed">
                              {faq.answer}
                            </p>
                          </div>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* No Results */}
          {filteredSections.length === 0 && (
            <div className="text-center py-12">
              <HelpCircle className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-400 mb-2">
                Aradığınız soru bulunamadı
              </h3>
              <p className="text-gray-500">
                Farklı anahtar kelimeler deneyin veya kategorileri değiştirin
              </p>
            </div>
          )}

          {/* Contact CTA */}
          <div className="max-w-4xl mx-auto mt-16">
            <div className="bg-gradient-to-r from-primary-500/10 to-primary-600/10 rounded-xl p-8 border border-primary-500/20 text-center">
              <h3 className="text-2xl font-bold text-white mb-4">
                Sorunuzu bulamadınız mı?
              </h3>
              <p className="text-gray-300 mb-6">
                Bizimle iletişime geçin, size yardımcı olmaktan mutluluk duyarız
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/contact"
                  className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-all duration-300 hover:scale-105 shadow-lg flex items-center justify-center"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  İletişime Geç
                </a>
                <a
                  href="https://discord.gg/aedonv"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white hover:bg-gray-200 text-dark-900 px-6 py-3 rounded-lg font-medium transition-all duration-300 hover:scale-105 shadow-lg flex items-center justify-center"
                >
                  <Users className="w-5 h-5 mr-2" />
                  Discord'a Katıl
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FAQ;
